/* ==========================================================================
   Flexara Founding List
   - Preserves the existing Klaviyo list subscription and signup event.
   - Registers the same email with KickoffLabs campaign 195763.
   - Replaces the old post-signup survey with an immediate referral dialog.
   - Uses only public browser endpoints. No private API keys belong here.
   ========================================================================== */
(function () {
  'use strict';

  var KLAVIYO_PUBLIC_KEY = 'U9vQbn';
  var KLAVIYO_REVISION = '2024-10-15';
  var REFERRAL_CAMPAIGN = 'founding_batch';
  var CONFIRMATION_KEY = 'flexara_waitlist_confirmation';
  var KOL_TIMEOUT_MS = 7000;
  var SHARE_MESSAGE = 'I just joined the founding list for Flexara Hydration. It’s serious hydration built with sensitive guts in mind. Thought you might want to check it out:';

  var attribution = (function () {
    var out = {};
    try {
      var params = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (key) {
        var value = params.get(key);
        if (value) out['flexara_' + key] = String(value).slice(0, 200);
      });
      if (document.referrer) out.flexara_referrer = document.referrer.slice(0, 300);
      out.flexara_landing_page = (window.location.pathname + window.location.search).slice(0, 300);
    } catch (error) {
      /* Attribution is best effort and must never block signup. */
    }
    return out;
  })();

  var referringSocialId = (function () {
    try {
      return (new URLSearchParams(window.location.search).get('kid') || '').slice(0, 80);
    } catch (error) {
      return '';
    }
  })();

  var dialog = null;
  var lastTrigger = null;
  var sharedEmail = '';
  var currentLead = null;

  function klaviyo(resource, payload) {
    return fetch(
      'https://a.klaviyo.com/client/' + resource + '/?company_id=' + encodeURIComponent(KLAVIYO_PUBLIC_KEY),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(payload)
      }
    );
  }

  function subscribe(listId, email, captureLocation, lead) {
    var properties = Object.assign(
      {
        source: 'flexara_waitlist_page',
        campaign: REFERRAL_CAMPAIGN,
        product: 'flexara_hydration',
        flexara_capture_location: captureLocation
      },
      attribution,
      lead ? leadProperties(lead) : {}
    );

    if (referringSocialId) {
      properties['Kickoff Labs Referred By Social ID'] = referringSocialId;
    }

    return klaviyo('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'flexara_waitlist_page',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email, properties: properties }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    });
  }

  function updateProfile(email, properties) {
    return klaviyo('profiles', {
      data: {
        type: 'profile',
        attributes: { email: email, properties: properties }
      }
    });
  }

  function trackEvent(name, email, extraProperties) {
    return klaviyo('events', {
      data: {
        type: 'event',
        attributes: {
          properties: Object.assign({}, attribution, extraProperties || {}),
          metric: { data: { type: 'metric', attributes: { name: name } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }
      }
    }).catch(function (error) {
      console.warn('[Flexara] Klaviyo event failed:', error);
    });
  }

  function emitAnalyticsEvent(name, captureLocation) {
    var detail = { capture_location: captureLocation };
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (key) {
      var value = attribution['flexara_' + key];
      if (value) detail[key] = value;
    });
    try {
      window.dispatchEvent(new CustomEvent('flexara:' + name, { detail: detail }));
    } catch (error) {
      console.warn('[Flexara] Browser analytics event failed:', error);
    }
  }

  function listConfigured(listId) {
    return listId && listId.indexOf('KLAVIYO_LIST_ID_HERE') === -1;
  }

  function waitForKickoffLabs() {
    if (window._kol && window._kol.leads && typeof window._kol.leads.addLead === 'function') {
      return Promise.resolve(window._kol);
    }

    return new Promise(function (resolve, reject) {
      var startedAt = Date.now();
      var interval = window.setInterval(function () {
        if (window._kol && window._kol.leads && typeof window._kol.leads.addLead === 'function') {
          window.clearInterval(interval);
          resolve(window._kol);
        } else if (Date.now() - startedAt >= KOL_TIMEOUT_MS) {
          window.clearInterval(interval);
          reject(new Error('KickoffLabs did not become ready.'));
        }
      }, 100);
    });
  }

  function registerLead(email, captureLocation) {
    return waitForKickoffLabs().then(function (kol) {
      return kol.leads.addLead(
        { email: email, __source: 'flexara_' + captureLocation },
        'flexara_waitlist_page'
      );
    });
  }

  function lookupLead(email) {
    return fetch('https://leads.kickofflabs.com/lead/195763?email=' + encodeURIComponent(email), {
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('KickoffLabs lead lookup failed.');
      return response.json();
    }).then(function (lead) {
      if (!leadSocialId(lead)) throw new Error('KickoffLabs lead has no social ID.');
      return lead;
    });
  }

  function registerOrRecoverLead(email, captureLocation) {
    return registerLead(email, captureLocation).catch(function (registrationError) {
      return lookupLead(email).catch(function () { throw registrationError; });
    });
  }

  function leadSocialId(lead) {
    if (!lead) return '';
    if (lead.social_id) return String(lead.social_id);
    if (typeof lead.id === 'string') return lead.id;
    return '';
  }

  function leadReferralCount(lead) {
    if (!lead) return 0;
    if (Array.isArray(lead.referrals)) return lead.referrals.length;
    var count = Number(lead.referrals);
    if (!Number.isFinite(count)) count = Number(lead.contest_score);
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }

  function referralUrl(lead) {
    var socialId = leadSocialId(lead);
    if (!socialId) return '';
    var url = new URL('https://flexara.com/');
    url.searchParams.set('kid', socialId);
    url.searchParams.set('utm_source', 'referral');
    url.searchParams.set('utm_medium', 'share');
    url.searchParams.set('utm_campaign', REFERRAL_CAMPAIGN);
    return url.toString();
  }

  function milestoneStatus(count) {
    if (count >= 10) {
      return { target: 10, title: '10-referral milestone reached', copy: 'You’ve unlocked $10 toward your first Flexara order. The free-box reward is limited to the first 50 people to reach 10 successful referrals. We’ll confirm qualification before your first paid order.', property: '10-referral milestone reached; free-box qualification pending' };
    }
    if (count >= 3) {
      return { target: 10, title: (10 - count) + ' more ' + ((10 - count) === 1 ? 'referral' : 'referrals') + ' to reach the free-box milestone', copy: 'You’ve unlocked $10 toward your first Flexara order. The free-box reward is limited to the first 50 people to reach 10 successful referrals.', property: '$10 toward first Flexara order' };
    }
    return { target: 3, title: count === 0 ? 'Refer 3 friends' : (3 - count) + ' more ' + ((3 - count) === 1 ? 'referral' : 'referrals') + ' to unlock $10', copy: 'Refer 3 friends who join the Flexara Founding List and unlock $10 toward your first Flexara order.', property: 'No milestone yet' };
  }

  function leadProperties(lead) {
    var socialId = leadSocialId(lead);
    var count = leadReferralCount(lead);
    var properties = {
      'Kickoff Labs Social ID': socialId,
      'KickoffLabs Lead ID': String(lead && lead.id ? lead.id : socialId),
      'KickoffLabs Referral URL': referralUrl(lead),
      'KickoffLabs Referral Count': count,
      'KickoffLabs Reward Level': milestoneStatus(count).property,
      'KickoffLabs Signup Date': String((lead && lead.when) || new Date().toISOString())
    };
    if (referringSocialId) properties['Kickoff Labs Referred By Social ID'] = referringSocialId;
    return properties;
  }

  function saveConfirmationHandoff(lead, email, captureLocation) {
    var socialId = leadSocialId(lead);
    try {
      var state = {
        socialId: socialId,
        count: leadReferralCount(lead),
        createdAt: Date.now()
      };
      if (!socialId && email) {
        state.pendingEmail = email;
        state.captureLocation = captureLocation;
      }
      window.sessionStorage.setItem(CONFIRMATION_KEY, JSON.stringify(state));
      return true;
    } catch (error) {
      console.warn('[Flexara] Confirmation handoff unavailable:', error);
      return false;
    }
  }

  function openDialog(trigger) {
    if (!dialog) return;
    lastTrigger = trigger || document.activeElement;
    document.body.classList.add('ffb-referral-open');
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    var closeButton = dialog.querySelector('[data-ffb-referral-close]');
    if (closeButton) closeButton.focus();
  }

  function closeDialog() {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('ffb-referral-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
  }

  function setDialogState(state) {
    if (!dialog) return;
    var loading = dialog.querySelector('[data-ffb-referral-loading]');
    var share = dialog.querySelector('[data-ffb-referral-share]');
    var error = dialog.querySelector('[data-ffb-referral-error]');
    if (loading) loading.hidden = state !== 'loading';
    if (share) share.hidden = state !== 'ready';
    if (error) error.hidden = state !== 'error';
  }

  function renderLead(lead) {
    if (!dialog) return;
    currentLead = lead;
    var url = referralUrl(lead);
    if (!url) {
      setDialogState('error');
      return;
    }

    var count = leadReferralCount(lead);
    var milestone = milestoneStatus(count);
    var countNode = dialog.querySelector('[data-ffb-referral-count]');
    var countLabel = dialog.querySelector('[data-ffb-referral-count-label]');
    var progress = dialog.querySelector('[data-ffb-referral-progress]');
    var fill = dialog.querySelector('[data-ffb-referral-progress-fill]');
    var nextTitle = dialog.querySelector('[data-ffb-referral-next-title]');
    var nextCopy = dialog.querySelector('[data-ffb-referral-next-copy]');
    var urlInput = dialog.querySelector('[data-ffb-referral-url]');
    var whatsapp = dialog.querySelector('[data-ffb-share="whatsapp"]');
    var sms = dialog.querySelector('[data-ffb-share="sms"]');
    var email = dialog.querySelector('[data-ffb-share="email"]');
    var body = SHARE_MESSAGE + '\n\n' + url;

    if (countNode) countNode.textContent = String(count);
    if (countLabel) countLabel.textContent = count === 1 ? 'friend referred' : 'friends referred';
    if (progress) {
      progress.setAttribute('aria-valuemax', String(milestone.target));
      progress.setAttribute('aria-valuenow', String(Math.min(count, milestone.target)));
    }
    if (fill) fill.style.width = Math.min(100, (count / milestone.target) * 100) + '%';
    if (nextTitle) nextTitle.textContent = milestone.title;
    if (nextCopy) nextCopy.textContent = milestone.copy;
    if (urlInput) urlInput.value = url;
    if (whatsapp) whatsapp.href = 'https://wa.me/?text=' + encodeURIComponent(body);
    if (sms) sms.href = (/iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'sms:&body=' : 'sms:?body=') + encodeURIComponent(body);
    if (email) email.href = 'mailto:?subject=' + encodeURIComponent('Join me on the Flexara Founding List') + '&body=' + encodeURIComponent(body);

    setDialogState('ready');
  }

  function fallbackCopy(url, done) {
    var input = dialog && dialog.querySelector('[data-ffb-referral-url]');
    if (!input) return;
    input.value = url;
    input.focus();
    input.select();
    try {
      document.execCommand('copy');
      done();
    } catch (error) {
      console.warn('[Flexara] Copy failed:', error);
    }
  }

  function copyReferralLink(button) {
    var url = referralUrl(currentLead);
    if (!url) return;
    function copied() {
      var original = button.textContent;
      button.textContent = 'Copied';
      window.setTimeout(function () { button.textContent = original; }, 1800);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(copied).catch(function () { fallbackCopy(url, copied); });
    } else {
      fallbackCopy(url, copied);
    }
  }

  function persistReferralProperties(email, lead) {
    return updateProfile(email, leadProperties(lead)).then(function (response) {
      if (!response.ok && response.status !== 202) throw new Error('Klaviyo profile update failed.');
    }).catch(function (error) {
      console.warn('[Flexara] Referral properties were not saved to Klaviyo:', error);
    });
  }

  function resolveReferral(email, captureLocation) {
    setDialogState('loading');
    return lookupLead(email).catch(function () { return registerLead(email, captureLocation); }).then(function (lead) {
      renderLead(lead);
      return persistReferralProperties(email, lead).then(function () {
        if (saveConfirmationHandoff(lead)) {
          window.setTimeout(function () { window.location.assign('/pages/waitlist-confirmed'); }, 150);
        }
        return lead;
      });
    }).catch(function (error) {
      console.error('[Flexara] KickoffLabs signup failed:', error);
      setDialogState('error');
      return null;
    });
  }

  function initCapture(block) {
    var listId = block.getAttribute('data-klaviyo-list');
    var captureLocation = block.getAttribute('data-ffb-capture-location') || 'unknown';
    var form = block.querySelector('[data-ffb-email-form]');
    if (!form) return;
    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('button[type="submit"]');
    var label = button && button.querySelector('[data-label]');
    var defaultLabel = label ? label.textContent : 'Join the Founding List';
    var message = block.querySelector('[data-ffb-message]');
    var success = block.querySelector('[data-ffb-success]');

    function setMessage(text, isError) {
      if (!message) return;
      message.textContent = text || '';
      if (isError) message.setAttribute('data-state', 'error');
      else message.removeAttribute('data-state');
    }

    function setLoading(isLoading) {
      if (!button) return;
      button.disabled = isLoading;
      if (label) label.textContent = isLoading ? 'Joining…' : defaultLabel;
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      setMessage('', false);
      var email = ((input && input.value) || '').trim();

      if (!email || !input.checkValidity()) {
        setMessage('Please enter a valid email address.', true);
        if (input) input.focus();
        return;
      }
      if (!listConfigured(listId)) {
        setMessage('Signups are not connected yet.', true);
        return;
      }

      setLoading(true);
      var referralPromise = registerOrRecoverLead(email, captureLocation).then(
        function (lead) { return { lead: lead, error: null }; },
        function (error) { return { lead: null, error: error }; }
      );

      referralPromise
        .then(function (referralResult) {
          return subscribe(listId, email, captureLocation, referralResult.lead)
            .then(function (response) {
              return { response: response, referralResult: referralResult };
            });
        })
        .then(function (result) {
          var response = result.response;
          var referralResult = result.referralResult;
          if (!response.ok && response.status !== 202) {
            return response.text().then(function (body) { throw new Error(body || 'Subscription failed.'); });
          }

          sharedEmail = email;
          form.hidden = true;
          if (success) success.hidden = true;
          saveConfirmationHandoff(referralResult.lead, email, captureLocation);
          setDialogState('loading');

          var referralUpdate = Promise.resolve();
          if (referralResult.lead) {
            renderLead(referralResult.lead);
            referralUpdate = persistReferralProperties(email, referralResult.lead);
          } else {
            console.error('[Flexara] KickoffLabs signup failed:', referralResult.error);
            setDialogState('error');
          }

          var signupEvent = trackEvent('Founding Batch Signup', email, {
            flexara_capture_location: captureLocation,
            kickofflabs_referred_by_social_id: referringSocialId || null
          });
          emitAnalyticsEvent('waitlist_signup', captureLocation);
          Promise.race([
            Promise.allSettled([referralUpdate, signupEvent]),
            new Promise(function (resolve) { window.setTimeout(resolve, 1500); })
          ]).then(function () {
            window.setTimeout(function () { window.location.assign('/pages/waitlist-confirmed'); }, 150);
          });
        })
        .catch(function (error) {
          console.error('[Flexara] Klaviyo subscribe error:', error);
          setMessage('Something went wrong. Please try again.', true);
        })
        .finally(function () { setLoading(false); });
    });
  }

  function initDialog() {
    dialog = document.querySelector('[data-ffb-referral-dialog]');
    if (!dialog) return;
    var closeButton = dialog.querySelector('[data-ffb-referral-close]');
    var retryButton = dialog.querySelector('[data-ffb-referral-retry]');
    var copyButton = dialog.querySelector('[data-ffb-share="copy"]');

    if (closeButton) closeButton.addEventListener('click', closeDialog);
    if (copyButton) copyButton.addEventListener('click', function () { copyReferralLink(copyButton); });
    if (retryButton) {
      retryButton.addEventListener('click', function () {
        if (sharedEmail) resolveReferral(sharedEmail, 'retry');
      });
    }
    dialog.addEventListener('close', function () { document.body.classList.remove('ffb-referral-open'); });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog();
    });
  }

  function init() {
    initDialog();
    document.querySelectorAll('[data-ffb-capture]').forEach(initCapture);
    try {
      var params = new URLSearchParams(window.location.search);
      var state = JSON.parse(window.sessionStorage.getItem(CONFIRMATION_KEY) || 'null');
      if (params.get('recover_referral') === '1' && state && state.pendingEmail &&
          Number.isFinite(state.createdAt) && Date.now() - state.createdAt <= 1800000) {
        sharedEmail = state.pendingEmail;
        openDialog(document.querySelector('[data-ffb-referral-retry]'));
        resolveReferral(sharedEmail, state.captureLocation || 'retry');
      }
    } catch (error) {
      console.warn('[Flexara] Referral recovery unavailable:', error);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
